// TIP-611 — the searchable list. Four fields use it: requester country, subject
// country, freezone (20 options) and emirate (8).
// The panel is positioned in VIEWPORT coordinates, as in the design. The order
// form's body is overflow:auto, so a panel positioned inside the flow would be
// clipped by it for every field below the fold. It flips above the trigger when
// there is no room beneath.
// It is rendered through a PORTAL to document.body, and that is load-bearing.
// `.cb-modal` carries `animation: cb-pop ... both`; an animation that mentions
// `transform` makes its element a containing block for fixed descendants, for
// as long as the fill lasts — which, with fill-mode `both`, is forever. Left in
// the tree the panel would resolve its coordinates against the modal box and
// open shifted right and down by the modal's own offset from the viewport.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./counterpartyBriefs.css";
import { CbLabel } from "./CbField";

const PANEL_MAX = 290;
const PANEL_MIN = 160;

const Chevron = () => (
  <svg className="cb-combo-chevron" width="13" height="9" viewBox="0 0 12 8" fill="none" aria-hidden="true">
    <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CbCombobox = ({
  error,
  id,
  label,
  required,
  value,
  options = [],
  placeholder = "Select",
  searchPlaceholder = "Search",
  onChange,
  testId,
  // TIP-611 — a locked field: the answer is implied by another field, so it is
  // shown and not editable. Disabled rather than read-only, so the panel cannot
  // be opened by keyboard either.
  disabled = false,
}) => {
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [panel, setPanel] = useState({ top: 0, left: 0, width: 0, height: PANEL_MAX });

  const place = useCallback(() => {
    const el = btnRef.current;
    // jsdom has no layout, so getBoundingClientRect is all zeros. The panel
    // still renders; only its offsets are meaningless, which tests do not read.
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const flip = below < 180 && above > below;
    const height = Math.max(PANEL_MIN, Math.min(PANEL_MAX, flip ? above : below));
    // The panel is at least 240px, which is wider than a narrow trigger, so a
    // field in the right-hand column has to be pulled back inside the edge.
    const width = Math.max(r.width, 240);
    setPanel({
      top: flip ? Math.max(8, r.top - height - 5) : r.bottom + 5,
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
      width,
      height,
    });
  }, []);

  // Measure once the panel is actually in the DOM, before paint, so it never
  // shows for a frame at the previous field's coordinates.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      // The panel is portalled out of the field, so it is not inside rootRef.
      // Without this second test, a mousedown in the search box counts as a
      // click outside and closes the panel before a character is typed.
      const inTrigger = rootRef.current && rootRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => `${o.label} ${o.sub || ""}`.toLowerCase().includes(q));
  }, [options, query]);

  const selected = options.find((o) => o.value === value) || null;

  const pick = (v) => {
    onChange && onChange(v);
    setOpen(false);
    setQuery("");
  };

  const toggle = () => {
    // Belt and braces alongside the disabled attribute: a click synthesised in
    // a test, or a programmatic one, must not open a locked field either.
    if (disabled) return;
    if (open) {
      setOpen(false);
      setQuery("");
      return;
    }
    place();
    setQuery("");
    setHighlight(0);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) pick(filtered[highlight].value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div
      className={`cb-field${error ? " cb-field--error" : ""}${disabled ? " cb-field--disabled" : ""}`}
      ref={rootRef}
    >
      <CbLabel htmlFor={id} label={label} required={required} />
      <button
        type="button"
        id={id}
        ref={btnRef}
        data-testid={testId}
        className={`cb-combo-trigger${error ? " cb-combo-trigger--error" : ""}`}
        // `aria-invalid` is not supported on a button's implicit role, so the
        // backend's complaint is announced by pointing at the message instead
        // — which a screen reader reads out with the control.
        aria-describedby={error && id ? `${id}-error` : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
      >
        {selected && selected.leading && (
          <span className="cb-combo-flag" aria-hidden="true">
            {selected.leading}
          </span>
        )}
        <span className={`cb-combo-value${selected ? "" : " is-placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <Chevron />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="cb-combo-panel"
          style={{
            top: panel.top,
            left: panel.left,
            width: panel.width,
            maxHeight: panel.height,
          }}
        >
          <div className="cb-combo-search">
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input
              autoFocus
              type="text"
              className="cb-combo-search-input"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={onKeyDown}
            />
          </div>
          <div className="cb-combo-list" role="listbox" aria-label={label}>
            {filtered.map((o, i) => (
              <div
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`cb-combo-option${i === highlight ? " is-highlighted" : ""}${
                  o.value === value ? " is-selected" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o.value);
                }}
              >
                {o.leading && (
                  <span className="cb-combo-flag" aria-hidden="true">
                    {o.leading}
                  </span>
                )}
                <span className="cb-combo-option-label">{o.label}</span>
                {o.sub && <span className="cb-combo-option-sub">{o.sub}</span>}
              </div>
            ))}
            {filtered.length === 0 && <div className="cb-combo-empty">No results</div>}
          </div>
        </div>,
        document.body
      )}

      {/* TIP-611 — the backend's own message about this field. */}
      {error && (
        <p id={id ? `${id}-error` : undefined} className="cb-field-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default CbCombobox;
